import type { IPortfolioDefDashboardInputQuery } from 'generated/graphql';
import { seeMoreDropdownClass } from '../../../technical/SeeMoreDropDown/SeeMoreDropdown.tsx';
import { createNewPortfolioActions, selectableItemsClass, sortPortfolios } from './PortfolioDefCards.utils.tsx';
import { IPortfolioDefinitionType } from 'generated/graphql';
import { useFeedback } from 'components/technical/Feedback/UseFeedback.tsx';
import { useRef, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { usePrevious } from '../../../UsePrevious.tsx';
import Selecto from 'react-selecto';
import { Box, Grid, Menu, Stack } from '@mui/joy';
import HeaderBar from 'components/technical/HeaderBar/HeaderBar.tsx';
import PortfolioCard, { gridWidth } from './PortfolioCard.tsx';
import type { ReactElement } from 'react';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GInput from 'components/technical/inputs/GInput/GInput.tsx';

import type { OnSelectEvent } from 'react-selecto';
import { GCardMenuButton } from 'components/technical/GCard/GCardMenuButton.tsx';
import DialogDropdown from 'components/technical/DialogDropdown/DialogDropdown.tsx';
import { useNavigate } from 'react-router';
import StaticMultiAutocomplete from 'components/technical/inputs/Autocomplete/StaticMultiAutocomplete.tsx';

type InputQuery = IPortfolioDefDashboardInputQuery['portfolio'];
const cardHeight = '11rem';

const isClickOnDropdown = (e: OnSelectEvent<unknown>): boolean => {
  const { inputEvent } = e;
  if (!(inputEvent instanceof MouseEvent)) {
    return false;
  }

  const { target } = inputEvent;
  if (!target) {
    return false;
  }

  const node = target as HTMLElement;
  return node.matches(`.${seeMoreDropdownClass}, .${seeMoreDropdownClass} *`);
};

const validPagePortfolios = (def: { type: IPortfolioDefinitionType }) =>
  def.type === IPortfolioDefinitionType.Real || def.type === IPortfolioDefinitionType.Rebalanced;

const TagFilter = ({
  selectedTags,
  onTagsChange,
  allTags,
}: { selectedTags: string[]; onTagsChange: (tags: string[]) => void; allTags: string[] }): ReactElement => {
  const tagOptions = allTags.map((tag) => ({
    label: tag,
    value: tag,
    key: tag,
    searchText: tag,
  }));

  return (
    <StaticMultiAutocomplete
      placeholder="Filter by tags..."
      options={tagOptions}
      value={selectedTags}
      onChange={onTagsChange}
      label=""
      width="normal"
      limitTags={2}
      variant="outlined"
    />
  );
};

export const PortfolioDefCards = ({
  defs,
  accounts,
  subFunds,
  selectedIds,
  setSelectedIds,
  allTags,
}: {
  defs: InputQuery['definitions'];
  accounts: InputQuery['accounts'];
  subFunds: InputQuery['subFunds'];
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  allTags: string[];
}): ReactElement => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const { showInfoMessage } = useFeedback();
  const selectoRef = useRef<Selecto | null>(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const selectionContainerRef = useRef<HTMLDivElement | null>(null);

  const validPortfolios = sortPortfolios(defs.filter((def) => validPagePortfolios(def)));

  const searchLower = searchTerm.toLowerCase();
  let filteredPortfolios = !searchTerm.trim()
    ? validPortfolios
    : validPortfolios.filter(
        (portfolio) =>
          portfolio.name.toLowerCase().includes(searchLower) ||
          portfolio.description?.toLowerCase().includes(searchLower)
      );

  if (selectedTags.length > 0) {
    filteredPortfolios = filteredPortfolios.filter((portfolio) =>
      selectedTags.every((selectedTag) => portfolio.tags.some((tag) => tag.toLowerCase() === selectedTag.toLowerCase()))
    );
  }

  useEffect(() => {
    const portfolioDefIds = defs.filter((def) => validPagePortfolios(def)).map((port) => port.id);

    // it's possible that we selected some items and then removed them
    const existingIds = new Set(portfolioDefIds);
    setSelectedIds((ids) => {
      return ids.filter((id) => existingIds.has(id));
    });
  }, [defs, setSelectedIds]);

  useEffect(() => {
    const onScroll = () => {
      selectoRef.current?.checkScroll();
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const prevSelectedCount = usePrevious(selectedIds.length);
  useEffect(() => {
    if (prevSelectedCount > 0) {
      return;
    }

    if (selectedIds.length === 0) {
      return;
    }

    showInfoMessage({
      msg: "Press 'ctrl' to select more portfolios",
      preventDuplicates: true,
    });
  }, [prevSelectedCount, selectedIds, showInfoMessage]);

  return (
    <Box ref={selectionContainerRef} minHeight={'100%'}>
      <Selecto
        ref={selectoRef}
        selectableTargets={[`.${selectableItemsClass}`]}
        selectByClick={true}
        selectFromInside={true}
        toggleContinueSelect={'ctrl'}
        hitRate={50}
        scrollOptions={{
          container: document.documentElement,
          getScrollPosition: ({ container }) => {
            return [container.scrollLeft, container.scrollTop];
          },
        }}
        onScroll={(e) => {
          // required for selected area to move when we scroll the window
          document.documentElement.scrollBy(e.direction[0] * 10, e.direction[1] * 10);
        }}
        onSelect={(e) => {
          if (isClickOnDropdown(e)) {
            selectoRef.current?.setSelectedTargets([]);
            setSelectedIds([]);
            return;
          }

          setSelectedIds(e.selected.map((el) => el.dataset.id!));
        }}
      />
      <HeaderBar
        title="Portfolios"
        sx={{
          cursor: 'default',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TagFilter selectedTags={selectedTags} onTagsChange={setSelectedTags} allTags={allTags} />
          <GInput
            placeholder="Search portfolios..."
            value={searchTerm}
            onChange={(value) => setSearchTerm(value || '')}
            startDecorator={<SearchRoundedIcon />}
            size="sm"
            width="xl2"
          />
        </Stack>
      </HeaderBar>

      <Grid container>
        {filteredPortfolios.map((def) => (
          <PortfolioCard
            key={def.id}
            cardHeight={cardHeight}
            selected={selectedIds.includes(def.id)}
            def={def}
            accounts={accounts}
            subFunds={subFunds.list}
            allTags={allTags}
          />
        ))}

        <DialogDropdown>
          <GCardMenuButton height={cardHeight} gridWidth={gridWidth} />
          <Menu
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, -30],
                },
              },
            ]}
          >
            {createNewPortfolioActions({ navigate, accounts, subFunds: subFunds.list })}
          </Menu>
        </DialogDropdown>
      </Grid>
    </Box>
  );
};
